/**
 * /portal/context (T-044, PR-23, PR-24, DATA-02, DATA-08, US-10).
 *
 * Read-only render of the 5-section context doc + an append-only "Your notes"
 * feed with a composer. Sub-fields that were promoted from a "Your notes"
 * entry carry a "Pulled" badge (PR-24, via context_docs.sections.provenance).
 * Your-notes entries cannot be edited or deleted by the subscriber
 * (append-only V1 invariant; RLS has no subscriber UPDATE/DELETE on
 * your_notes). Feed is reverse chronological.
 */

import { useCallback, useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { YourNote } from '@studio/api-schemas';
import {
  appendYourNote,
  fetchOwnContextDoc,
  fetchOwnSubscriber,
  fetchOwnYourNotes,
  type PortalContextDoc,
} from '@/lib/portal';
import { AsyncPanel, type AsyncState } from '@/components/portal/AsyncPanel';
import { toast } from '@/components/ui/toast';

export const Route = createFileRoute('/portal/context')({
  component: ContextPage,
});

interface ContextBundle {
  doc: PortalContextDoc | null;
  notes: YourNote[];
  subscriberId: string | null;
}

const SECTION_DEFS: ReadonlyArray<{
  key:
    | 'company'
    | 'audiences'
    | 'strategic_context'
    | 'voice_and_constraints'
    | 'initial_priorities';
  title: string;
  fields: ReadonlyArray<{ path: string; label: string }>;
}> = [
  {
    key: 'company',
    title: 'Company',
    fields: [
      { path: 'name', label: 'Name' },
      { path: 'stage', label: 'Stage' },
      { path: 'what_they_sell', label: 'What they sell' },
      { path: 'where_they_sell', label: 'Where they sell' },
    ],
  },
  {
    key: 'audiences',
    title: 'Audiences',
    fields: [
      { path: 'primary_buyer', label: 'Primary buyer' },
      { path: 'secondary_buyers', label: 'Secondary buyers' },
      { path: 'users', label: 'Users' },
      { path: 'who_not_serving', label: 'Who you are not serving' },
    ],
  },
  {
    key: 'strategic_context',
    title: 'Strategic context',
    fields: [
      { path: 'current_one_liner', label: 'Current one-liner' },
      { path: 'current_positioning', label: 'Current positioning' },
      { path: 'competitors_of_note', label: 'Competitors of note' },
      { path: 'whats_working', label: "What's working" },
      { path: 'biggest_blocker', label: 'Biggest blocker' },
    ],
  },
  {
    key: 'voice_and_constraints',
    title: 'Voice & constraints',
    fields: [
      { path: 'voice_attributes', label: 'Voice attributes' },
      { path: 'banned_phrases', label: 'Banned phrases' },
      { path: 'tone', label: 'Tone' },
      { path: 'language', label: 'Language' },
      { path: 'regulatory_constraints', label: 'Regulatory constraints' },
    ],
  },
  {
    key: 'initial_priorities',
    title: 'Initial priorities',
    fields: [
      { path: 'first_30_days', label: 'First 30 days' },
      { path: 'what_to_deliver_first', label: 'What to deliver first' },
      {
        path: 'what_to_avoid_first_month',
        label: 'What to avoid in the first month',
      },
    ],
  },
];

function ContextPage() {
  const [state, setState] = useState<AsyncState<ContextBundle>>({
    status: 'loading',
  });
  const [composer, setComposer] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [doc, notes, subscriber] = await Promise.all([
        fetchOwnContextDoc(),
        fetchOwnYourNotes(),
        fetchOwnSubscriber(),
      ]);
      setState({
        status: 'ready',
        data: { doc, notes, subscriberId: subscriber?.id ?? null },
      });
    } catch {
      setState({
        status: 'error',
        message: 'Could not load your context. Try again in a moment.',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function postNote(subscriberId: string) {
    if (composer.trim().length === 0) return;
    setPosting(true);
    try {
      await appendYourNote(subscriberId, composer.trim());
      setComposer('');
      toast.success('Added to your notes.');
      await load();
    } catch {
      toast.error('Could not save the note. Try again.');
    } finally {
      setPosting(false);
    }
  }

  return (
    <AsyncPanel state={state} loadingLabel="Loading your context...">
      {(d) => {
        const sections =
          (d.doc?.sections ?? {}) as Record<string, Record<string, unknown>>;
        const provenance =
          (d.doc?.sections.provenance ?? {}) as Record<
            string,
            { promoted_from_note_id: string }
          >;

        return (
          <div className="max-w-2xl space-y-10">
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
                Context doc
              </p>
              <h1 className="mb-2 font-display text-h2 italic leading-tight text-[color:var(--color-navy)]">
                Your context.
              </h1>
              <p className="max-w-xl text-sm leading-relaxed text-[color:var(--color-text-mute)]">
                This is Eric&apos;s primary reference before every task. He
                keeps it current as you learn more together. You can read it
                freely; to change anything, add to Your notes below and Eric
                folds in what is worth formalizing.
              </p>
            </div>

            <div className="space-y-6">
              {SECTION_DEFS.map((sec) => {
                const data = sections[sec.key] ?? {};
                const populated = sec.fields.filter(
                  (f) =>
                    data[f.path] !== undefined &&
                    data[f.path] !== null &&
                    String(data[f.path]).length > 0,
                );
                return (
                  <section
                    key={sec.key}
                    className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-5"
                  >
                    <h2 className="mb-4 border-b border-[color:var(--color-rule)] pb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
                      {sec.title}
                    </h2>
                    {populated.length === 0 ? (
                      <p className="text-sm text-[color:var(--color-text-mute)]">
                        Nothing here yet. This fills in as the work goes.
                      </p>
                    ) : (
                      <dl className="space-y-3">
                        {populated.map((f) => {
                          const pulled =
                            provenance[`/${sec.key}/${f.path}`] !== undefined;
                          const raw = data[f.path];
                          const value = Array.isArray(raw)
                            ? raw.join(', ')
                            : String(raw);
                          return (
                            <div key={f.path}>
                              <dt className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[color:var(--color-text-mute)]">
                                {f.label}
                                {pulled ? (
                                  <span className="inline-flex items-center rounded-sm border border-[color:var(--color-green-accent)] bg-[color:var(--color-green-accent-subtle)] px-1.5 py-0.5 text-[0.625rem] font-medium normal-case tracking-normal text-[color:var(--color-green-accent)]">
                                    Pulled from your notes
                                  </span>
                                ) : null}
                              </dt>
                              <dd className="mt-0.5 whitespace-pre-line text-sm text-[color:var(--color-navy)]">
                                {value}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    )}
                  </section>
                );
              })}
            </div>

            <section aria-label="Your notes">
              <div className="mb-1 flex flex-wrap items-center gap-3">
                <h2 className="font-display text-h3 italic text-[color:var(--color-navy)]">
                  Your notes
                </h2>
                <span className="inline-flex items-center rounded-sm border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.05em] text-[color:var(--color-text-mute)]">
                  Only you can write here
                </span>
              </div>
              <p className="mb-4 text-sm leading-relaxed text-[color:var(--color-text-mute)]">
                Think of something Eric should know? Add it here. Notes are
                kept as-is; Eric folds the useful ones into the context above.
              </p>

              {d.subscriberId ? (
                <div className="mb-6">
                  <textarea
                    aria-label="Add a note"
                    value={composer}
                    onChange={(e) => setComposer(e.target.value)}
                    rows={3}
                    placeholder="A thing you noticed, a change, a constraint..."
                    className="w-full rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] p-3 text-sm leading-relaxed text-[color:var(--color-navy)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
                  />
                  <button
                    type="button"
                    disabled={composer.trim().length === 0 || posting}
                    onClick={() => postNote(d.subscriberId as string)}
                    className="mt-2 inline-flex items-center justify-center rounded-sm bg-[color:var(--color-rose)] px-4 py-2 text-sm font-medium text-[color:var(--color-navy)] hover:opacity-90 disabled:opacity-50"
                  >
                    {posting ? 'Adding...' : 'Add note'}
                  </button>
                </div>
              ) : (
                <p className="mb-6 text-sm text-[color:var(--color-text-mute)]">
                  You are viewing as an operator; Your notes is a subscriber
                  surface.
                </p>
              )}

              {d.notes.length === 0 ? (
                <p className="text-sm text-[color:var(--color-text-mute)]">
                  No notes yet.
                </p>
              ) : (
                <ul className="space-y-3">
                  {d.notes.map((n) => (
                    <li
                      key={n.id}
                      className="rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] p-4"
                    >
                      <p className="whitespace-pre-line text-sm leading-relaxed text-[color:var(--color-navy)]">
                        {n.body}
                      </p>
                      <p className="mt-2 flex items-center gap-2 text-xs text-[color:var(--color-text-mute)]">
                        {new Date(n.created_at).toLocaleString('en-US')}
                        {n.promoted_to_context_at ? (
                          <span className="inline-flex items-center rounded-sm border border-[color:var(--color-green-accent)] bg-[color:var(--color-green-accent-subtle)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.05em] text-[color:var(--color-green-accent)]">
                            Folded into context
                          </span>
                        ) : null}
                      </p>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-5 rounded-md border border-[color:var(--color-rose)] bg-[color:var(--color-rose-subtle2)] px-4 py-3">
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-rose-dark)]">
                  Why you can&apos;t edit this directly
                </p>
                <p className="text-sm leading-relaxed text-[color:var(--color-navy)]">
                  Eric keeps a specific structure here so it stays consistent
                  and usable, including by the tools running on his end. Your
                  notes flow in as-is; he integrates them in a way that keeps
                  the format clean.
                </p>
              </div>
            </section>
          </div>
        );
      }}
    </AsyncPanel>
  );
}
